import React, { useEffect } from 'react';
import { Card, Spin, Button, Input, Typography, Select } from 'antd';
import type { StatusCreate, StatusUpdate } from '../types/status.types';
import { StatusOutcome } from '../types/status.types';
import { useTranslation } from 'react-i18next';
import { DefaultValues, Path } from 'react-hook-form';
import { ValidationError } from '../../../../../types/api-response.types';
import { useForm, Controller } from 'react-hook-form';
import { SaveOutlined } from '@ant-design/icons';

const { TextArea } = Input;

interface StatusFormProps {
  defaultValues?: DefaultValues<StatusCreate | StatusUpdate>;
  onFormSubmit: (data: StatusCreate | StatusUpdate, resetForm: () => void) => void;
  isLoading: boolean;
  validationErrors?: ValidationError[];
}

function StatusFormComponent({
  defaultValues,
  onFormSubmit,
  isLoading = false,
  validationErrors = [],
}: StatusFormProps) {
  const { t } = useTranslation();

  const { handleSubmit, control, reset, setValue, setError } = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) {
      setValue('candidateStep', defaultValues.candidateStep);
      setValue('stageDescription', defaultValues.stageDescription);
      setValue('statusCode', defaultValues.statusCode);
      setValue('statusOutcome', defaultValues.statusOutcome);
      setValue('purpose', defaultValues.purpose);
      setValue('actionBy', defaultValues.actionBy);
      setValue('actionRequired', defaultValues.actionRequired);
    }
  }, [defaultValues, setValue]);

  // Handle validation errors from API
  useEffect(() => {
    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => {
        setError(error.path as Path<StatusCreate | StatusUpdate>, {
          type: 'manual',
          message: error.message,
        });
      });
    }
  }, [validationErrors, setError]);

  const onSubmit = (data: StatusCreate | StatusUpdate) => {
    onFormSubmit(data, () => {
      reset();
    });
  };

  return (
    <Spin spinning={isLoading} tip={t('common.submittingForm')}>
      <Card className="shadow-lg">
        <form onSubmit={handleSubmit(onSubmit as any)}>
          {/* Row 1: Candidate Step and Status Code */}
          <div className="row mb-3">
            <div className="col-md-6 mb-3 mb-md-0">
              <label className="form-label">{t('statuses.candidateStep.label')}</label>
              <Controller
                name="candidateStep"
                control={control}
                rules={{
                  required: t('statuses.candidateStep.required'),
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Input
                      {...field}
                      placeholder={t('statuses.candidateStep.placeholder')}
                      status={error ? 'error' : ''}
                    />
                    {error && (
                      <Typography.Text type="danger" className="mt-1 d-block">
                        {error.message}
                      </Typography.Text>
                    )}
                  </div>
                )}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('statuses.statusCode.label')}</label>
              <Controller
                name="statusCode"
                control={control}
                rules={{
                  required: t('statuses.statusCode.required'),
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Input
                      {...field}
                      placeholder={t('statuses.statusCode.placeholder')}
                      status={error ? 'error' : ''}
                    />
                    {error && (
                      <Typography.Text type="danger" className="mt-1 d-block">
                        {error.message}
                      </Typography.Text>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          {/* Row 2: Status Outcome and Action By */}
          <div className="row mb-3">
            <div className="col-md-6 mb-3 mb-md-0">
              <label className="form-label">{t('statuses.statusOutcome.label')}</label>
              <Controller
                name="statusOutcome"
                control={control}
                rules={{
                  required: t('statuses.statusOutcome.required'),
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Select
                      {...field}
                      placeholder={t('statuses.statusOutcome.placeholder')}
                      status={error ? 'error' : ''}
                      style={{ width: '100%' }}
                    >
                      <Select.Option value={StatusOutcome.Success}>
                        {StatusOutcome.Success}
                      </Select.Option>
                      <Select.Option value={StatusOutcome.Failure}>
                        {StatusOutcome.Failure}
                      </Select.Option>
                      <Select.Option value={StatusOutcome.NA}>
                        {StatusOutcome.NA}
                      </Select.Option>
                    </Select>
                    {error && (
                      <Typography.Text type="danger" className="mt-1 d-block">
                        {error.message}
                      </Typography.Text>
                    )}
                  </div>
                )}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('statuses.actionBy.label')}</label>
              <Controller
                name="actionBy"
                control={control}
                rules={{
                  required: t('statuses.actionBy.required'),
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Input
                      {...field}
                      placeholder={t('statuses.actionBy.placeholder')}
                      status={error ? 'error' : ''}
                    />
                    {error && (
                      <Typography.Text type="danger" className="mt-1 d-block">
                        {error.message}
                      </Typography.Text>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          {/* Row 3: Stage Description and Purpose */}
          <div className="row mb-3">
            <div className="col-md-6 mb-3 mb-md-0">
              <label className="form-label">{t('statuses.stageDescription.label')}</label>
              <Controller
                name="stageDescription"
                control={control}
                rules={{
                  required: t('statuses.stageDescription.required'),
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <TextArea
                      {...field}
                      placeholder={t('statuses.stageDescription.placeholder')}
                      status={error ? 'error' : ''}
                      rows={3}
                    />
                    {error && (
                      <Typography.Text type="danger" className="mt-1 d-block">
                        {error.message}
                      </Typography.Text>
                    )}
                  </div>
                )}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('statuses.purpose.label')}</label>
              <Controller
                name="purpose"
                control={control}
                rules={{
                  required: t('statuses.purpose.required'),
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <TextArea
                      {...field}
                      placeholder={t('statuses.purpose.placeholder')}
                      status={error ? 'error' : ''}
                      rows={3}
                    />
                    {error && (
                      <Typography.Text type="danger" className="mt-1 d-block">
                        {error.message}
                      </Typography.Text>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          {/* Row 4: Action Required (Full Width) */}
          <div className="mb-3">
            <label className="form-label">{t('statuses.actionRequired.label')}</label>
            <Controller
              name="actionRequired"
              control={control}
              rules={{
                required: t('statuses.actionRequired.required'),
              }}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <TextArea
                    {...field}
                    placeholder={t('statuses.actionRequired.placeholder')}
                    status={error ? 'error' : ''}
                    rows={3}
                  />
                  {error && (
                    <Typography.Text type="danger" className="mt-1 d-block">
                      {error.message}
                    </Typography.Text>
                  )}
                </div>
              )}
            />
          </div>

          <Button
            type="primary"
            size="large"
            htmlType="submit"
            className="mt-4 float-end"
            loading={isLoading}
            icon={<SaveOutlined />}
          >
            {t('common.save')}
          </Button>
        </form>
      </Card>
    </Spin>
  );
}

// Custom comparison function for memoization
function areEqual(prevProps: StatusFormProps, nextProps: StatusFormProps): boolean {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onFormSubmit === nextProps.onFormSubmit &&
    JSON.stringify(prevProps.defaultValues) === JSON.stringify(nextProps.defaultValues) &&
    JSON.stringify(prevProps.validationErrors) === JSON.stringify(nextProps.validationErrors)
  );
}

export default React.memo(StatusFormComponent, areEqual);
